import React, { useState, useRef, useEffect } from 'react';
import { Eye, EyeOff, ChevronDown, ChevronRight, Briefcase, GraduationCap, Award, FolderOpen, Languages, Trophy, Wrench, Image, User, Mail, FileText } from 'lucide-react';

// Section configuration with icons and labels
const SECTION_CONFIG = {
  photo: { label: 'Profile Photo', icon: User, type: 'single' },
  banner: { label: 'Banner Image', icon: Image, type: 'single' },
  contact: { label: 'Contact Information', icon: Mail, type: 'single' },
  summary: { label: 'About/Summary', icon: FileText, type: 'single' },
  professional_summary: { label: 'Professional Summary', icon: FileText, type: 'single' },
  experience: { label: 'Work Experience', icon: Briefcase, type: 'array' },
  education: { label: 'Education', icon: GraduationCap, type: 'array' },
  skills: { label: 'Skills', icon: Wrench, type: 'array' },
  certifications: { label: 'Certifications', icon: Award, type: 'array' },
  projects: { label: 'Projects', icon: FolderOpen, type: 'array' },
  training: { label: 'Training', icon: GraduationCap, type: 'array' },
  languages: { label: 'Languages', icon: Languages, type: 'array' },
  awards: { label: 'Awards', icon: Trophy, type: 'array' }
};

// Get display name for an item
const getItemDisplayName = (section, item, index) => {
  switch (section) {
    case 'experience':
      return item.title && item.company 
        ? `${item.title} at ${item.company}` 
        : item.title || item.company || `Experience ${index + 1}`;
    case 'education':
      return item.degree && item.institution 
        ? `${item.degree} - ${item.institution}` 
        : item.institution || item.degree || `Education ${index + 1}`;
    case 'certifications':
      return item.name || item.title || `Certification ${index + 1}`;
    case 'projects':
      return item.name || `Project ${index + 1}`;
    case 'training':
      return item.name || `Training ${index + 1}`;
    case 'languages':
      return item.name || `Language ${index + 1}`;
    case 'awards':
      return item.title || item.name || `Award ${index + 1}`;
    case 'skills':
      return typeof item === 'string' ? item : (item.name || `Skill ${index + 1}`);
    default:
      return `Item ${index + 1}`;
  }
};

// Get date range for an item
const getItemDateRange = (section, item) => {
  if (section === 'experience' || section === 'education') {
    const start = item.start_year || item.start_date || '';
    const end = item.is_current ? 'Present' : (item.end_year || item.end_date || '');
    if (start || end) {
      return `${start}${start && end ? ' - ' : ''}${end}`;
    }
  }
  if (section === 'certifications') {
    return item.issue_year || item.date || '';
  }
  return '';
};

const ContentVisibilityDropdown = ({ resume, onUpdateVisibility }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize hidden_content if not present
  const hiddenContent = resume?.hidden_content || {
    sections: {},
    items: {}
  };

  // Toggle section visibility
  const toggleSection = (sectionKey) => {
    const newHiddenContent = {
      ...hiddenContent,
      sections: {
        ...hiddenContent.sections,
        [sectionKey]: !hiddenContent.sections?.[sectionKey]
      }
    };
    onUpdateVisibility(newHiddenContent);
  };

  // Toggle individual item visibility
  const toggleItem = (sectionKey, index) => {
    const itemKey = `${sectionKey}_${index}`;
    const newHiddenContent = {
      ...hiddenContent,
      items: {
        ...hiddenContent.items,
        [itemKey]: !hiddenContent.items?.[itemKey]
      }
    };
    onUpdateVisibility(newHiddenContent);
  };

  // Toggle section expansion in dropdown
  const toggleSectionExpand = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Check if section has content
  const sectionHasContent = (sectionKey) => {
    const config = SECTION_CONFIG[sectionKey];
    if (config.type === 'single') {
      if (sectionKey === 'photo') return !!resume?.photo_url;
      if (sectionKey === 'banner') return !!resume?.banner_url;
      if (sectionKey === 'contact') return !!(resume?.contact_email || resume?.contact_phone || resume?.location);
      if (sectionKey === 'summary') return !!resume?.summary;
      if (sectionKey === 'professional_summary') return !!resume?.professional_summary;
    }
    const items = resume?.[sectionKey];
    return Array.isArray(items) && items.length > 0;
  };

  // Count hidden items
  const countHiddenItems = () => {
    let count = 0;
    // Count hidden sections
    Object.keys(hiddenContent.sections || {}).forEach(key => {
      if (hiddenContent.sections[key]) count++;
    });
    // Count hidden items
    Object.keys(hiddenContent.items || {}).forEach(key => {
      if (hiddenContent.items[key]) count++;
    });
    return count;
  };

  const hiddenCount = countHiddenItems();

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {hiddenCount > 0 ? (
          <EyeOff className="w-4 h-4 text-orange-500" />
        ) : (
          <Eye className="w-4 h-4" />
        )}
        <span>Content</span>
        {hiddenCount > 0 && (
          <span className="px-1.5 py-0.5 text-xs bg-orange-100 text-orange-700 rounded-full">
            {hiddenCount}
          </span>
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 z-50 mt-2 w-80 max-h-96 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Content Visibility</h3>
            <p className="text-xs text-gray-500 mt-1">
              Uncheck items to hide them from your resume. Data is preserved.
            </p>
          </div>

          <div className="p-2">
            {Object.entries(SECTION_CONFIG).map(([sectionKey, config]) => {
              if (!sectionHasContent(sectionKey)) return null;

              const Icon = config.icon;
              const isSectionHidden = hiddenContent.sections?.[sectionKey];
              const isExpanded = expandedSections[sectionKey];
              const items = config.type === 'array' ? (resume?.[sectionKey] || []) : null;

              return (
                <div key={sectionKey} className="mb-1">
                  {/* Section Header */}
                  <div className="flex items-center gap-2 p-2 rounded hover:bg-gray-50">
                    {config.type === 'array' && items?.length > 0 && (
                      <button
                        onClick={() => toggleSectionExpand(sectionKey)}
                        className="p-0.5 hover:bg-gray-200 rounded"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                    )}
                    
                    <label className="flex items-center gap-2 flex-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!isSectionHidden}
                        onChange={() => toggleSection(sectionKey)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <Icon className={`w-4 h-4 ${isSectionHidden ? 'text-gray-400' : 'text-gray-600'}`} />
                      <span className={`text-sm ${isSectionHidden ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                        {config.label}
                      </span>
                      {config.type === 'array' && items?.length > 0 && (
                        <span className="text-xs text-gray-400">({items.length})</span>
                      )}
                    </label>
                  </div>

                  {/* Individual Items */}
                  {config.type === 'array' && isExpanded && items?.length > 0 && (
                    <div className="ml-6 pl-4 border-l-2 border-gray-100">
                      {items.map((item, index) => {
                        const itemKey = `${sectionKey}_${index}`;
                        const isItemHidden = hiddenContent.items?.[itemKey];
                        const displayName = getItemDisplayName(sectionKey, item, index);
                        const dateRange = getItemDateRange(sectionKey, item);

                        return (
                          <label
                            key={itemKey}
                            className="flex items-start gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={!isItemHidden && !isSectionHidden}
                              disabled={isSectionHidden}
                              onChange={() => toggleItem(sectionKey, index)}
                              className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                            />
                            <div className="flex-1 min-w-0">
                              <span className={`text-sm block truncate ${
                                isItemHidden || isSectionHidden ? 'text-gray-400 line-through' : 'text-gray-700'
                              }`}>
                                {displayName}
                              </span>
                              {dateRange && (
                                <span className="text-xs text-gray-400">{dateRange}</span>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {hiddenCount > 0 && (
            <div className="p-3 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => {
                  onUpdateVisibility({ sections: {}, items: {} });
                }}
                className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Show All Content
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentVisibilityDropdown;
